import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { INTERNAL_ADMIN_ROUTES, INTERNAL_ADMIN_WORKSPACES, cvsiDocsUrl } from '@cvsi/shared';
import { useStaff } from '../lib/staffContext';
import { CompanySwitcher } from './CompanySwitcher';

/** Workspace dropdowns for CVSI staff, matching the customer portal chrome. */
export function TopNavigation() {
  const { session, signOut } = useStaff();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!session) return null;

  const byPath = new Map<string, (typeof INTERNAL_ADMIN_ROUTES)[number]>(
    INTERNAL_ADMIN_ROUTES.map((route) => [route.path, route]),
  );
  const renderLink = (path: string) => {
    const route = byPath.get(path);
    if (!route) return null;
    return (
      <NavLink
        key={route.path}
        to={route.path}
        className={({ isActive }) => (isActive ? 'active' : '')}
        onClick={() => {
          setOpenMenu(null);
          setMobileOpen(false);
        }}
      >
        {route.label}
      </NavLink>
    );
  };

  const toggleMenu = (label: string) => setOpenMenu((current) => (current === label ? null : label));
  let host = session.baseUrl;
  try { host = new URL(session.baseUrl).host; } catch { /* keep raw baseUrl if unparsable */ }

  return (
    <header className="top-navigation">
      <Link className="portal-brand" to="/tenants" aria-label="CVSI Admin home">
        <span className="portal-logo-crop"><img src="/cvsi-logo.png" alt="CVSI by Cruze Rideshare Inc." /></span>
      </Link>

      <button
        type="button"
        className="mobile-nav-toggle"
        aria-expanded={mobileOpen}
        aria-controls="internal-admin-navigation"
        aria-label={mobileOpen ? 'Close portal navigation' : 'Open portal navigation'}
        onClick={() => {
          setMobileOpen((current) => !current);
          setOpenMenu(null);
        }}
      >
        <span aria-hidden="true">{mobileOpen ? '×' : '☰'}</span>
        <span>{mobileOpen ? 'Close' : 'Menu'}</span>
      </button>

      <nav
        id="internal-admin-navigation"
        className={`top-nav-groups${mobileOpen ? ' is-open' : ''}`}
        aria-label="Internal admin workspaces"
      >
        {INTERNAL_ADMIN_WORKSPACES.map((group) => {
          const open = openMenu === group.label;
          const menuId = `workspace-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          return (
            <div className="workspace-menu" key={group.label}>
              <button
                type="button"
                className="workspace-trigger"
                aria-expanded={open}
                aria-controls={menuId}
                onClick={() => toggleMenu(group.label)}
              >
                {group.label} <span aria-hidden="true">{open ? '▴' : '▾'}</span>
              </button>
              {open && (
                <div id={menuId} className="workspace-dropdown">
                  {group.paths.map(renderLink)}
                </div>
              )}
            </div>
          );
        })}

        <div className="workspace-menu utilities-menu">
          <button
            type="button"
            className="workspace-trigger"
            aria-expanded={openMenu === 'More'}
            aria-controls="workspace-more"
            onClick={() => toggleMenu('More')}
          >
            More <span aria-hidden="true">{openMenu === 'More' ? '▴' : '▾'}</span>
          </button>
          {openMenu === 'More' && (
            <div id="workspace-more" className="workspace-dropdown workspace-dropdown-wide">
              <CompanySwitcher />
              <div className="workspace-meta">
                <span>Operator</span>
                <strong>{session.actorId}</strong>
              </div>
              <div className="workspace-meta">
                <span>API</span>
                <strong>{host}</strong>
              </div>
              <a href={cvsiDocsUrl('Admin Portal')} onClick={() => setMobileOpen(false)}>CVSI Docs</a>
              <a href="https://cruzenc.com/" onClick={() => setMobileOpen(false)}>Back to Website</a>
              <button type="button" className="workspace-signout" onClick={signOut}>Sign out</button>
            </div>
          )}
        </div>
      </nav>

      {(openMenu || mobileOpen) && (
        <button
          className="workspace-scrim"
          aria-label="Close navigation options"
          onClick={() => {
            setOpenMenu(null);
            setMobileOpen(false);
          }}
        />
      )}
    </header>
  );
}
